/**
 * PhaseOverlay — per-phase floating overlay rendered over the Environment
 * canvas. Switched by `activePhase`, backed by real graph/run data derived
 * in EnvironmentGraphPage from the existing `environment-graph-d3` query
 * (no new fetch). When data is absent each overlay shows an honest
 * empty/sparse state — never a fabricated provenance hash (NFR-4).
 *
 * Cycle-105.8 sprint-2 — FR-E-1 (Tasks 230.3 + 230.4).
 *
 *   WEAVE  → ComposeSummary  (node / edge counts by type)
 *   RUN    → ExecuteRail     (RUN·LIVE + EXECUTION LOG + PROVENANCE chain)
 *   ATTEST → ProveSummary    (VERIFICATION LADDER + LineageAxis)
 *   RECALL → MemoryLegend    (memory-tier legend with counts)
 */

import { cn } from '../../../lib/utils';
import type { PhaseId } from './PhaseTabs';

// ── Data contract (computed by the page, presentational here) ──

export interface ComposeData {
  nodeCount: number;
  edgeCount: number;
  byType: Array<{ type: string; count: number; color: string }>;
}

export interface RunEntry {
  id: string;
  label: string;
  status: string;
}

export interface ProvenanceEntry {
  id: string;
  label: string;
  /** Real content/attestation hash from backend metadata, if present. */
  hash?: string;
}

export interface ProveRung {
  label: string;
  count: number;
  tone: 'verified' | 'provisional' | 'pending';
}

export interface LineageEntry {
  id: string;
  label: string;
}

export interface MemoryTierEntry {
  label: string;
  color: string;
  count: number;
}

export interface PhaseOverlayData {
  compose: ComposeData;
  live: number;
  runs: RunEntry[];
  provenance: ProvenanceEntry[];
  proveRungs: ProveRung[];
  lineage: LineageEntry[];
  memoryTiers: MemoryTierEntry[];
}

interface PhaseOverlayProps {
  activePhase: PhaseId;
  data: PhaseOverlayData;
  className?: string;
}

// ── Shared card frame (pinned top-left over the canvas) ──

const PANEL_STYLE: React.CSSProperties = {
  background: 'color-mix(in oklch, var(--e-env-bg-card) 92%, transparent)',
  border: '1px solid var(--e-env-border)',
  backdropFilter: 'blur(8px)',
};

function OverlayCard({
  title,
  badge,
  badgeTone,
  testid,
  children,
}: {
  title: string;
  badge?: string;
  badgeTone?: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      // top-14 (not top-3) so the card never covers the D3 canvas top-3/left-3
      // focus-mode indicator (D3GraphCanvas.tsx:779) or top-right legend (C1).
      className="pointer-events-auto absolute top-14 left-3 w-56 rounded-lg px-3 py-2.5"
      style={PANEL_STYLE}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-[8px] font-mono uppercase tracking-widest"
          style={{ color: 'var(--e-env-text-muted)' }}
        >
          {title}
        </span>
        {badge && (
          <span
            className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
            style={{
              color: badgeTone ?? 'oklch(0.668 0.162 175)',
              background: 'var(--e-env-bg-active)',
            }}
          >
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[10px] font-mono py-1.5"
      style={{ color: 'var(--e-env-text-faint)' }}
    >
      {children}
    </div>
  );
}

// ── WEAVE — ComposeSummary ──

function ComposeSummary({ data }: { data: ComposeData }) {
  return (
    <OverlayCard title="Compose" testid="overlay-compose">
      <div className="flex items-baseline gap-3 mb-2">
        <div>
          <div className="text-[18px] font-semibold leading-none" style={{ color: 'var(--e-env-text-bright)' }}>
            {data.nodeCount}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--e-env-text-faint)' }}>
            nodes
          </div>
        </div>
        <div>
          <div className="text-[18px] font-semibold leading-none" style={{ color: 'var(--e-env-text-bright)' }}>
            {data.edgeCount}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--e-env-text-faint)' }}>
            links
          </div>
        </div>
      </div>
      {data.byType.length === 0 ? (
        <EmptyLine>No modules composed yet.</EmptyLine>
      ) : (
        <div className="space-y-1">
          {data.byType.map((t) => (
            <div key={t.type} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
              <span className="text-[10px] font-mono capitalize" style={{ color: 'var(--e-env-text)' }}>
                {t.type}
              </span>
              <span className="ml-auto text-[10px] font-mono tabular-nums" style={{ color: 'var(--e-env-text-muted)' }}>
                {t.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </OverlayCard>
  );
}

// ── RUN — ExecuteRail ──

function ExecuteRail({
  live,
  runs,
  provenance,
}: {
  live: number;
  runs: RunEntry[];
  provenance: ProvenanceEntry[];
}) {
  const statusColor: Record<string, string> = {
    running: 'oklch(0.568 0.160 152)',
    active: 'oklch(0.548 0.172 198)',
    completed: 'oklch(0.688 0.165 72)',
    verified: 'oklch(0.688 0.165 72)',
    failed: 'oklch(0.615 0.208 22)',
  };
  return (
    <OverlayCard
      title="Execute"
      badge={live > 0 ? `RUN · LIVE ${live}` : 'IDLE'}
      badgeTone={live > 0 ? 'oklch(0.568 0.160 152)' : 'oklch(0.480 0.007 265)'}
      testid="overlay-execute"
    >
      <div className="text-[8px] font-mono uppercase tracking-widest mb-1" style={{ color: 'var(--e-env-text-dim)' }}>
        Execution Log
      </div>
      {runs.length === 0 ? (
        <EmptyLine>No runs recorded yet.</EmptyLine>
      ) : (
        <div className="space-y-1 mb-2">
          {runs.slice(0, 5).map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: statusColor[r.status] ?? 'oklch(0.480 0.007 265)' }}
              />
              <span className="text-[10px] font-mono truncate" style={{ color: 'var(--e-env-text)' }}>
                {r.label}
              </span>
              <span className="ml-auto text-[8px] font-mono uppercase" style={{ color: 'var(--e-env-text-faint)' }}>
                {r.status}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[8px] font-mono uppercase tracking-widest mb-1 mt-2" style={{ color: 'var(--e-env-text-dim)' }}>
        Provenance
      </div>
      {provenance.length === 0 ? (
        <EmptyLine>No attestations recorded yet.</EmptyLine>
      ) : (
        <div className="space-y-1">
          {provenance.slice(0, 4).map((p, i) => (
            <div key={p.id} className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono" style={{ color: 'var(--e-env-text-dim)' }}>
                {i === 0 ? '└' : '├'}
              </span>
              <span
                className="text-[9px] font-mono truncate"
                style={{ color: 'oklch(0.688 0.165 72)' }}
                title={p.hash ?? p.id}
              >
                {p.hash ? `${p.hash.slice(0, 12)}…` : `ref ${p.id.slice(0, 8)}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </OverlayCard>
  );
}

// ── ATTEST — ProveSummary + LineageAxis ──

function ProveSummary({ rungs, lineage }: { rungs: ProveRung[]; lineage: LineageEntry[] }) {
  const toneColor: Record<ProveRung['tone'], string> = {
    verified: 'oklch(0.688 0.165 72)',
    provisional: 'oklch(0.655 0.178 52)',
    pending: 'oklch(0.480 0.007 265)',
  };
  const totalProved = rungs.reduce((acc, r) => acc + r.count, 0);
  return (
    <OverlayCard title="Prove" testid="overlay-prove">
      <div className="text-[8px] font-mono uppercase tracking-widest mb-1.5" style={{ color: 'var(--e-env-text-dim)' }}>
        Verification Ladder
      </div>
      {totalProved === 0 ? (
        <EmptyLine>Nothing verified yet.</EmptyLine>
      ) : (
        <div className="space-y-1 mb-2">
          {rungs.map((r) => (
            <div key={r.label} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: toneColor[r.tone] }} />
              <span className="text-[10px] font-mono" style={{ color: 'var(--e-env-text)' }}>
                {r.label}
              </span>
              <span className="ml-auto text-[10px] font-mono tabular-nums" style={{ color: 'var(--e-env-text-muted)' }}>
                {r.count}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[8px] font-mono uppercase tracking-widest mb-1 mt-2" style={{ color: 'var(--e-env-text-dim)' }}>
        Lineage Axis
      </div>
      {lineage.length === 0 ? (
        <EmptyLine>No lineage yet.</EmptyLine>
      ) : (
        <div className="relative pl-3">
          <div
            className="absolute left-1 top-1 bottom-1 w-px"
            style={{ background: 'oklch(0.688 0.165 72 / 0.4)' }}
          />
          <div className="space-y-1">
            {lineage.slice(0, 5).map((l) => (
              <div key={l.id} className="flex items-center gap-1.5">
                <span
                  className="absolute left-[1px] w-1.5 h-1.5 rounded-full -translate-x-[2.5px]"
                  style={{ background: 'oklch(0.688 0.165 72)' }}
                />
                <span className="text-[9px] font-mono truncate" style={{ color: 'var(--e-env-text)' }}>
                  {l.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </OverlayCard>
  );
}

// ── RECALL — MemoryLegend ──

function MemoryLegend({ tiers }: { tiers: MemoryTierEntry[] }) {
  return (
    <OverlayCard title="Recall" badge="Memory" testid="overlay-memory">
      {tiers.every((t) => t.count === 0) ? (
        <EmptyLine>No memory accumulated yet.</EmptyLine>
      ) : (
        <div className="space-y-1.5">
          {tiers.map((t) => (
            <div key={t.label} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: t.color, opacity: 0.85 }} />
              <span className="text-[10px] font-mono uppercase tracking-wide" style={{ color: 'var(--e-env-text)' }}>
                {t.label}
              </span>
              <span className="ml-auto text-[10px] font-mono tabular-nums" style={{ color: 'var(--e-env-text-muted)' }}>
                {t.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </OverlayCard>
  );
}

// ── Switcher ──

export function PhaseOverlay({ activePhase, data, className }: PhaseOverlayProps) {
  let body: React.ReactNode = null;
  switch (activePhase) {
    case 'WEAVE':
      body = <ComposeSummary data={data.compose} />;
      break;
    case 'RUN':
      body = <ExecuteRail live={data.live} runs={data.runs} provenance={data.provenance} />;
      break;
    case 'ATTEST':
      body = <ProveSummary rungs={data.proveRungs} lineage={data.lineage} />;
      break;
    case 'RECALL':
      body = <MemoryLegend tiers={data.memoryTiers} />;
      break;
  }
  return (
    <div
      className={cn('pointer-events-none absolute inset-0 z-30', className)}
      aria-live="polite"
      data-testid="phase-overlay"
    >
      {body}
    </div>
  );
}
